/* eslint-disable no-shadow */
import { createSelector } from 'reselect';

import { filtersItems } from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersSelectors';
import { dialogs as dialogsSelector } from '@Containers/Dialogs/DialogsSelectors';

import { getGroupsFromChunksForCurrentSorting } from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersUtils';

import { ascending, descending, occurrence, byValue } from '@Containers/ResultsFilters/ResultsFiltersConstants';

export function filter(state, ownProps) {
  const { filterId } = ownProps;

  return filtersItems(state)?.get(filterId);
}

export function filterTitle(state, ownProps) {
  const { filterId } = ownProps;

  return filtersItems(state)?.getIn([filterId, 'title']);
}

export function chunksCount(state, ownProps) {
  return filter(state, ownProps)?.get('chunksCount');
}

export function activeChunkIndexAsString(state, ownProps) {
  return dialogsSelector(state).getIn([ownProps.id, 'activeChunkIndex']).toString();
}

export function activeChunkIndexAsInteger(state, ownProps) {
  return dialogsSelector(state).getIn([ownProps.id, 'activeChunkIndex']);
}

export function sorting(state, ownProps) {
  return dialogsSelector(state).getIn([ownProps.id, 'sorting']);
}

export function disableAscending(state, ownProps) {
  return sorting(state, ownProps) === ascending;
}

export function disableDescending(state, ownProps) {
  return sorting(state, ownProps) === descending;
}

export function sortingType(state, ownProps) {
  return dialogsSelector(state).getIn([ownProps.id, 'sortingType']);
}

export function maxSize(state, ownProps) {
  const filterItem = filter(state, ownProps);

  if (!filterItem) {
    return undefined;
  }

  const pathToFirstGroupId = ['chunks', occurrence, descending, '0', 'ids', '0'];
  const idOfFirstGroup = filterItem.getIn(pathToFirstGroupId);

  const result = filterItem.getIn(['chunks', occurrence, descending, '0', 'items', idOfFirstGroup, 'size']);

  return parseInt(result, 10);
}

export function customGroups(state, ownProps) {
  return filter(state, ownProps)?.get('customGroups');
}

export function customGroupsWithByValueSortingFactory() {
  const selector = createSelector(
    [sortingType, customGroups],

    (sortingType, customGroups) => customGroups && sortingType === byValue
  );
  return selector;
}

export function customGroupsData(state, ownProps) {
  return filter(state, ownProps)?.get('customGroupsData');
}

export function activeCustomGroupIndex(state, ownProps) {
  return dialogsSelector(state).getIn([ownProps.id, 'activeCustomGroupIndex']);
}

export function activeCustomGroupLetter(state, ownProps) {
  const data = customGroupsData(state, ownProps);
  const activeIndex = activeCustomGroupIndex(state, ownProps);
  const activeGroup = data && data.find(({ index }) => index === activeIndex);
  return activeGroup && activeGroup.title;
}

export function activeChunkFactory() {
  const activeChunk = createSelector(
    [activeChunkIndexAsString, filter, sortingType, sorting, activeCustomGroupLetter, customGroupsWithByValueSortingFactory()],

    (activeChunkIndexAsString, filter, sortingType, sorting, activeCustomGroupLetter, customGroupsWithByValueSorting) => {
      if (!filter) {
        return undefined;
      }

      const key = customGroupsWithByValueSorting ? activeCustomGroupLetter : activeChunkIndexAsString;
      const ids = filter.getIn(['chunks', sortingType, sorting, key, 'ids']);

      if (!ids) {
        return null;
      }

      const items = filter.getIn(['chunks', sortingType, sorting, key, 'items']);

      return ids.map((id) => items.get(id));
    }
  );

  return activeChunk;
}

function onlySelectedMapOfIdsReduceFn(res, item) {
  const selected = item.get('selected');
  if (!selected) {
    return res;
  }

  const id = item.get('id');
  res[id] = true;

  return res;
}

export function propsForDisableControlsFactory() {
  const allFromGroupsAndChunksUnselected = createSelector([filter, sortingType, sorting], (filter, sortingType, sorting) => {
    if (!filter) {
      return undefined;
    }

    const chunks = getGroupsFromChunksForCurrentSorting(filter, sortingType, sorting);
    const groupsThatAreNotInChunks = filter.get('groups').filter((item) => {
      const id = item.get('id');

      return !chunks.get(id);
    });

    const someFromGroups = groupsThatAreNotInChunks.some((item) => item.get('selected'));

    const someFromChunks = chunks.some((item) => item.get('selected'));

    return !someFromGroups && !someFromChunks;
  });

  const selectedCount = createSelector([filter, sortingType, sorting], (filter, sortingType, sorting) => {
    if (!filter) {
      return undefined;
    }

    const chunks = getGroupsFromChunksForCurrentSorting(filter, sortingType, sorting);
    const nonChunk =
      filter.has('clearGroupValues') && filter.get('clearGroupValues') ? filter.get('clearGroupValues') : filter.get('groups');
    const groupsThatAreNotInChunks = nonChunk.filter((item) => {
      const id = item.get('id');

      return !chunks.get(id);
    });

    const unickIdsMapFromChunks = chunks.reduce(onlySelectedMapOfIdsReduceFn, {});
    const unickIdsMapFromGroups = groupsThatAreNotInChunks.reduce(onlySelectedMapOfIdsReduceFn, {});

    return Object.keys(unickIdsMapFromChunks).length + Object.keys(unickIdsMapFromGroups).length;
  });

  return {
    selectedCount,
    disableClearSelected: allFromGroupsAndChunksUnselected,
    disableLimitTo: allFromGroupsAndChunksUnselected,
    disableExclude: allFromGroupsAndChunksUnselected,
  };
}
